import { useCallback } from 'react';
import { useApiBookingsGetCollection } from '@ae/data-access';
import { Locale, useLocale } from '@ae/shared';
import { useDate, useGetProdDomain, useTranslation } from '@ae/shared';
import { LinkGetter, useLink, useMe } from '../hooks';

export enum ThematicLink {
  New,
  HotTub,
  Redu,
  Best,
  Promotion,
  Chalets,
  Dog,
  PoolTable,
  Nature,
  Swimmingpool,
  Sauna,
  SaunaView,
  Hotel,
  Fireplace,
  Stays2026,
  UnusualAccommodations,
  Castles,
  ByTheWater,
  Autumn,
  DeerRutting,
  View,
  NewYear,
  Jacuzzi,
  SaintsDay,
  Romantic,
  VoucherOrder,
  HiddenGem,
  EarlyBooking,
  EarlyBookingBitly,
  DiscountBitly,
  Winter,
  Wellness,
  PrivateGarden,
  Dinant,
  Cottage,
  JacuzziView,
  DiscountThematic,
  Entertainment,
  SpringThematic,
  NewThematic,
  Relax,
  Superhost,
  LastMinuteThematic,
  SummerThematic,
}

export const useGetThematicLink = () => {
  const locale = useLocale();
  const { dayjs } = useDate();
  const { t } = useTranslation('thematic');
  const getProdDomain = useGetProdDomain();
  const { authenticated } = useMe();
  const { data: bookings } = useApiBookingsGetCollection({
    query: { enabled: authenticated },
  });

  const getLink = useCallback(
    (link: ThematicLink): LinkGetter => {
      switch (link) {
        case ThematicLink.New:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/nouveautes`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/nieuw`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/neu`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/new`;
                default:
                  return '';
              }
            },

            alt: t('thematic.popular.new.label'),
            title: t('thematic.popular.new.label'),
          };

        case ThematicLink.Jacuzzi:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Vacances-Jacuzzi`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Vakantie-Jacuzzi`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Holidays-Jacuzzi`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Ferien-Jacuzzi`;
                default:
                  return '';
              }
            },

            alt: t('thematic.popular.jacuzyThematic.label'),
            title: t('thematic.popular.jacuzyThematic.label'),
          };

        case ThematicLink.View:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/terrasse-vue-imprenable`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/terras-met-uniek-uitzicht`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/terrasse-blick`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/terrace-breathtaking-view`;
                default:
                  return '';
              }
            },

            alt: t('thematic.popular.view.label'),
            title: t('thematic.popular.view.label'),
          };

        case ThematicLink.NewYear:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Nouvel-An`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Nieuwjaar`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Neujahr`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/New-Year`;
                default:
                  return '';
              }
            },

            alt: t('thematic.popular.newYear.label'),
            title: t('thematic.popular.newYear.label'),
          };

        case ThematicLink.DeerRutting:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/ecouter-brame-du-cerf`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/luisteren-naar-burlende-herten`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/hirschrohren`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/listening-to-the-belling-of-deer`;
                default:
                  return '';
              }
            },

            alt: t('thematic.popular.deerRutting.label'),
            title: t('thematic.popular.deerRutting.label'),
          };

        case ThematicLink.Autumn:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/automne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/herfst`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/herbst`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/autumn`;
                default:
                  return '';
              }
            },
          };
        case ThematicLink.SaintsDay:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/2024-10-31_to_2024-11-03/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/2024-10-31_to_2024-11-03?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/herbst`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/autumn`;
                default:
                  return '';
              }
            },
          };

        case ThematicLink.HotTub:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Vacances-Jacuzzi`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Vakantie-Jacuzzi`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Ferien-Jacuzzi`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Holidays-Jacuzzi`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.hotTub.label'),
            title: t('thematic.popular.hotTub.label'),
          };
        case ThematicLink.Redu:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Redu?TriRecherche=Distance-ASC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Redu?TriRecherche=Distance-ASC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Redu?TriRecherche=Distance-ASC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Redu?TriRecherche=Distance-ASC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.redu.label'),
            title: t('thematic.popular.redu.label'),
          };
        case ThematicLink.Best:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/464_maisons-de-vacances-en-Ardenne/les-mieux-notees/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/464_vakantiehuizen-in-de-Ardennen/de-best-beoordeelde/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/464_ferienwohnungen-in-den-Ardennen/bestbewerteten/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/464_holiday-Houses-in-the-Ardennes/top-rated/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.best.label'),
            title: t('thematic.popular.best.label'),
          };

        case ThematicLink.Promotion:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/Promotie/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.promo.label'),
            title: t('thematic.popular.promo.label'),
          };

        case ThematicLink.Chalets:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Chalets`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Chalets`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Chalets`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Chalets`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.chalet.label'),
            title: t('thematic.popular.chalet.label'),
          };
        case ThematicLink.Cottage:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/338_maisons-de-vacances-en-Ardenne/Chalets/Feu-ouvert/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/338_vakantiehuizen-in-de-Ardennen/Chalets/Open-haard/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/338_ferienwohnungen-in-den-Ardennen/Chalets/Kamin/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/338_holiday-Houses-in-the-Ardennes/Chalets/Fireplace/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.cottage.label'),
            title: t('thematic.popular.cottage.label'),
          };

        case ThematicLink.Entertainment:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/485_maisons-de-vacances-en-Ardenne/divertissement?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/485_vakantiehuizen-in-de-Ardennen/Ontspanning/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/485_ferienwohnungen-in-den-Ardennen/Unterhaltung/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/485_holiday-Houses-in-the-Ardennes/Entertainment/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.entertainment.label'),
            title: t('thematic.popular.entertainment.label'),
          };

        case ThematicLink.JacuzziView:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/363_maisons-de-vacances-en-Ardenne/Jacuzzi/terrasse-avec-vue/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/363_vakantiehuizen-in-de-Ardennen/Jacuzzi/terras-met-uitzicht/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/363_ferienwohnungen-in-den-Ardennen/Jacuzzi/terrasse-mit-Blick/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/363_holiday-Houses-in-the-Ardennes/Jacuzzi/terrace-with-view/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.jacuzzi.label'),
            title: t('thematic.popular.jacuzzi.label'),
          };
        case ThematicLink.DiscountThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/Promotie/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.discountThematic.label'),
            title: t('thematic.popular.discountThematic.label'),
          };
        case ThematicLink.Dog:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/vacances-avec-chien`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/vakantie-met-hond`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/urlaub-mit-hund`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/pet-friendly-accommodation`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.dog.label'),
            title: t('thematic.popular.dog.label'),
          };
        case ThematicLink.Superhost:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/superhotes`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/superhosts`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/superhosts`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/superhosts`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.superhost.label'),
            title: t('thematic.popular.superhost.label'),
          };
        case ThematicLink.Relax:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/363_maisons-de-vacances-en-Ardenne/Jacuzzi/terrasse-avec-vue/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/363_vakantiehuizen-in-de-Ardennen/Jacuzzi/terras-met-uitzicht/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/363_ferienwohnungen-in-den-Ardennen/Jacuzzi/terrasse-mit-Blick/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/363_holiday-Houses-in-the-Ardennes/Jacuzzi/terrace-with-view/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.relax.label'),
            title: t('thematic.popular.relax.label'),
          };
        case ThematicLink.PoolTable:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/billard/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/biljart/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/billardtisch/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/billiard/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.poolTable.label'),
            title: t('thematic.popular.poolTable.label'),
          };
        case ThematicLink.Swimmingpool:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/gites-avec-piscine`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/vakantiehuizen-met-zwembad`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/ferienhauser-mit-schwimmbad`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/holiday-houses-with-swimmingpool`;
                default:
                  return '';
              }
            },
            alt: t('thematic.wellness.swimmingpool.label'),
            title: t('thematic.wellness.swimmingpool.label'),
          };
        case ThematicLink.Sauna:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/Sejour-avec-Sauna`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/Verblijven-met-Sauna`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/Aufenthalt-mit-Sauna`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/Stays-with-Sauna`;
                default:
                  return '';
              }
            },
            alt: t('thematic.wellness.sauna.label'),
            title: t('thematic.wellness.sauna.label'),
          };

        case ThematicLink.SaunaView:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-53p/Week-end/0000-00-00/364_maisons-de-vacances-en-Ardenne/Sauna/terrasse-avec-vue/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-53p/Weekend/0000-00-00/364_vakantiehuizen-in-de-Ardennen/Sauna/terras-met-uitzicht/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-53p/Weekend/0000-00-00/364_holiday-Houses-in-the-Ardennes/Sauna/terrace-with-view/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-53p/Wochenende/0000-00-00/364_ferienwohnungen-in-den-Ardennen/Sauna/terrasse-mit-Blick/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.sauna.label'),
            title: t('thematic.popular.sauna.label'),
          };
        case ThematicLink.Hotel:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/confort-hotel`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/zoals-op-hotel`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/hotel-comfort`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/wie-im-hotel`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.hotel.label'),
            title: t('thematic.popular.hotel.label'),
          };

        case ThematicLink.HiddenGem:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/perles-rares`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/pareltjes`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/hidden-gems`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/verborgene-schatze`;
                default:
                  return '';
              }
            },
            alt: t('thematic.hiddenGemThematic.title'),
            title: t('thematic.hiddenGemThematic.title'),
          };

        case ThematicLink.PrivateGarden:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/jardin-prive/les-mieux-notees/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/privetuin/de-best-beoordeelde/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/private-garden/top-rated/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/privater-garten/bestbewerteten/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.privateGarden.title'),
            title: t('thematic.privateGarden.title'),
          };
        case ThematicLink.Dinant:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/51_maisons-de-vacances-en-Ardenne/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/51_vakantiehuizen-in-de-Ardennen/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/51_holiday-Houses-in-the-Ardennes/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/51_ferienwohnungen-in-den-Ardennen/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.dinant.title'),
            title: t('thematic.dinant.title'),
          };

        case ThematicLink.EarlyBooking:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/2025-01-00/496_maisons-de-vacances-en-Ardenne/early-booking`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/2025-01-00/496_vakantiehuizen-in-de-Ardennen/vroegboekdeal`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/Promotion/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.earlyBookingThematic.title'),
            title: t('thematic.earlyBookingThematic.title'),
          };

        case ThematicLink.EarlyBookingBitly:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `https://bit.ly/early-booking-2025-2026`;
                case Locale.Nl:
                  return `https://bit.ly/vroegboekdeal-2025-2026`;
                default:
                  return '';
              }
            },
            alt: t('thematic.earlyBookingThematic.title'),
            title: t('thematic.earlyBookingThematic.title'),
          };
        case ThematicLink.DiscountBitly:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `https://bit.ly/last-minute-juin-juillet-2025`;
                case Locale.Nl:
                  return `https://bit.ly/last-minute-juni-juli-2025`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.discountThematic.label'),
            title: t('thematic.popular.discountThematic.label'),
          };

        case ThematicLink.Wellness:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/Jacuzzi/Sauna/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/Jacuzzi/Sauna/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/Jacuzzi/Sauna/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/Jacuzzi/Sauna/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.wellnessThematic.title'),
            title: t('thematic.wellnessThematic.title'),
          };

        case ThematicLink.Winter:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/hiver`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/winter`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/winter`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/winter`;
                default:
                  return '';
              }
            },
            alt: t('thematic.winterThematic.title'),
            title: t('thematic.winterThematic.title'),
          };

        case ThematicLink.Nature:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/se-ressourcer-en-ardenne`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/onthaasten-in-de-ardennen`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/relaxing-in-the-ardennes`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/entschleunigen-in-den-ardennen`;
                default:
                  return '';
              }
            },
            alt: t('thematic.natureThematic.title'),
            title: t('thematic.natureThematic.title'),
          };

        case ThematicLink.Fireplace:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/0000-00-00/Feu-ouvert/Chalets/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/0000-00-00/Open-haard/Chalets/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/0000-00-00/Fireplace/Chalets/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/0000-00-00/Kamin/Chalets/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.fireplace.label'),
            title: t('thematic.popular.fireplace.label'),
          };

        case ThematicLink.Stays2026:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Week-end/2026-01-00/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Weekend/2026-01-00/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Weekend/2026-01-00/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Wochenende/2026-01-00/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.popular.stays2026.label'),
            title: t('thematic.popular.stays2026.label'),
          };

        case ThematicLink.UnusualAccommodations:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/week-end-insolite`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/origneel-overnachten`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/originell-ubernachten`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/original-accommodation`;
                default:
                  return '';
              }
            },
            alt: t('thematic.wellness.unusualAccommodations.label'),
            title: t('thematic.wellness.unusualAccommodations.label'),
          };
        case ThematicLink.Castles:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/chateaux`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/kastelen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/schlosser`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/castles`;
                default:
                  return '';
              }
            },
            alt: t('thematic.wellness.castles.label'),
            title: t('thematic.wellness.castles.label'),
          };
        case ThematicLink.ByTheWater:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/bord-eau`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/huizen-aan-het-water`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/am-wasser`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/by-the-water`;
                default:
                  return '';
              }
            },
            alt: t('thematic.campagne.title'),
            title: t('thematic.campagne.title'),
          };

        case ThematicLink.Romantic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/week-end-en-amoureux`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/romantisch-weekendje-weg`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/romantikwochenende`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/romantic-weekend-breaks`;
                default:
                  return '';
              }
            },
            alt: t('footer.offer_a_gift'),
            title: t('footer.offer_a_gift'),
          };
        case ThematicLink.SpringThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-10p/Week-end/0000-00-00/jardin-prive/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-10p/Weekend/0000-00-00/privetuin/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-10p/Wochenende/0000-00-00/privater-garten/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-10p/Weekend/0000-00-00/private-garden/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.springThematic.title'),
            title: t('thematic.springThematic.title'),
          };

        case ThematicLink.LastMinuteThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/weekend-pas-cher`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/weekend-aan-scherpe-prijs`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/guenstiges-wochenende`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/weekend-for-tiny-price`;
                default:
                  return '';
              }
            },
            alt: t('thematic.lastMinuteThematic.title'),
            title: t('thematic.lastMinuteThematic.title'),
          };

        case ThematicLink.SummerThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/recherche/1-99p/Semaine/2025-08-00/jardin-prive/?TriRecherche=Satisfaction-DESC`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/zoeken/1-99p/Week/2025-08-00/privetuin/?TriRecherche=Satisfaction-DESC`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/suchen/1-99p/Woche/2025-08-00/privater-garten/?TriRecherche=Satisfaction-DESC`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/search/1-99p/Week/2025-08-00/private-garden/?TriRecherche=Satisfaction-DESC`;
                default:
                  return '';
              }
            },
            alt: t('thematic.summerThematic.title'),
            title: t('thematic.summerThematic.title'),
          };

        case ThematicLink.NewThematic:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/maisons-de-vacances-ardennes/nouveautes`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/vakantiehuizen-ardennen/nieuw`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/ferienhauser-ardennen/neu`;
                case Locale.En:
                  return `${getProdDomain(Locale.En)}/holiday-houses-ardennes/new`;
                default:
                  return '';
              }
            },
            alt: t('thematic.newThematic.title'),
            title: t('thematic.newThematic.title'),
          };

        case ThematicLink.VoucherOrder:
          return {
            href: () => {
              switch (locale) {
                case Locale.Fr:
                  return `${getProdDomain(Locale.Fr)}/cheque-cadeau`;
                case Locale.Nl:
                  return `${getProdDomain(Locale.Nl)}/cadeaubon-bestellen`;
                case Locale.De:
                  return `${getProdDomain(Locale.De)}/geschenkgutschein-bestellen`;
                case Locale.En:
                default:
                  return `${getProdDomain(Locale.En)}/gift-vouchers`;
              }
            },
            alt: t('home.voucherOrder.title'),
            title: t('home.voucherOrder.cta'),
          };

        default:
          throw Error('link not found');
      }
    },
    [authenticated, bookings, dayjs, getProdDomain, locale, t]
  );

  return useLink<ThematicLink>(getLink);
};
